import React, { useCallback, useState } from 'react';
import { Button, ButtonProps } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as Sentry from '@sentry/react';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import {
  downloadAudioFile,
  hasAudioUrl,
  resolveAudioUrl,
} from 'utils/methods/downloadAudioFile';

type AudioDownloadButtonProps = Omit<ButtonProps, 'onClick'> & {
  audioUrl?: string | null;
  label?: string;
  loadingLabel?: string;
  hideWhenEmpty?: boolean;
  showAlerts?: boolean;
};

const AudioDownloadButton: React.FC<AudioDownloadButtonProps> = ({
  audioUrl,
  label = 'Download Audio',
  loadingLabel = 'Downloading...',
  hideWhenEmpty = true,
  showAlerts = true,
  disabled,
  variant = 'outlined',
  size = 'small',
  startIcon = <DownloadIcon />,
  children,
  ...buttonProps
}) => {
  const showAlert = useAlert();
  const [downloading, setDownloading] = useState(false);
  const resolvedAudioUrl = resolveAudioUrl(audioUrl);
  const hasAudio = hasAudioUrl(audioUrl);

  const download = useCallback(async () => {
    if (!resolvedAudioUrl) return;
    setDownloading(true);
    try {
      const result = await downloadAudioFile(resolvedAudioUrl);
      if (showAlerts) {
        if (result.fallback) {
          showAlert(
            'Opened audio in a new tab. Use your browser to save the file.',
            'info',
          );
        } else {
          showAlert('Audio downloaded successfully', 'success');
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      if (showAlerts) {
        showAlert('Failed to download audio', 'error');
      }
    } finally {
      setDownloading(false);
    }
  }, [resolvedAudioUrl, showAlert, showAlerts]);

  if (hideWhenEmpty && !hasAudio) {
    return null;
  }

  return (
    <Button
      variant={variant}
      size={size}
      startIcon={startIcon}
      onClick={download}
      disabled={disabled || downloading || !hasAudio}
      {...buttonProps}
    >
      {children ?? (downloading ? loadingLabel : label)}
    </Button>
  );
};

export default AudioDownloadButton;
